import clsx from 'clsx';
import { type PropsWithChildren, type JSX } from 'react';
import { useIntl } from 'react-intl';

import {
  ArrowSmallDownIcon,
  SorterSmallDoubleIcon,
} from '@dynatrace/strato-icons';

import * as styles from './Sorting.sty.js';
import { Text } from '../../../../typography/text/Text.js';
import { dataTableMessages } from '../../messages.js';
import type { SortDirection } from '../../tanstack-table-core-override/src/index.js';

/**
 * Renders button within the column header to trigger sorting directly on header click.
 * @internal
 */
export const SortingArrowIndicators = (
  props: PropsWithChildren<{
    isSorted?: false | SortDirection;
    nextSortingOrder?: false | SortDirection;
    multiSortPriority?: number;
    rowDensity: 'default' | 'condensed' | 'comfortable';
  }>,
): JSX.Element => {
  const { isSorted, multiSortPriority, nextSortingOrder, rowDensity } = props;

  const intl = useIntl();
  const dataDtName = 'ColumnHeaderSortIcon';

  let dataDtProperties = 'state:clearSort';
  if (nextSortingOrder === 'asc') {
    dataDtProperties = 'state:ascending';
  } else if (nextSortingOrder === 'desc') {
    dataDtProperties = 'state:descending';
  }

  if (isSorted !== false) {
    const sortedDirectionIcon = (
      <ArrowSmallDownIcon
        data-sorticon
        data-dt-name={dataDtName}
        data-dt-properties={dataDtProperties}
        style={{ display: 'inline' }}
        className={clsx(
          styles.sortingArrow({ arrowUp: isSorted === 'asc', rowDensity }),
        )}
      />
    );
    if (multiSortPriority !== undefined) {
      return (
        <Text as="span" className={styles.multiSortIndicatorBubble}>
          {sortedDirectionIcon}
          <Text
            as="span"
            textStyle="small-emphasized"
            aria-hidden="true"
            className={styles.sortOrderIndicator}
          >
            {multiSortPriority}
          </Text>
        </Text>
      );
    }

    return sortedDirectionIcon;
  }

  return (
    <SorterSmallDoubleIcon
      data-sorticon
      aria-label={intl.formatMessage(dataTableMessages.sortableColumnIcon)}
      className={styles.sortingArrow({ rowDensity })}
      data-dt-name={dataDtName}
      data-dt-properties={dataDtProperties}
    />
  );
};
